'use client'

import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { HOME_EASE } from '@/features/home/components/home-motion'
import { useNumberFormat } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'
import { useChartData } from '@/service/chart.hook'
import type { Exchange } from '@/types/generated/common/types'
import { CandleFamily } from '@/types/generated/public_api'

const WIDTH = 320
const HEIGHT = 120
const PAD_Y = 10
const HOUR_S = 3600
const BARS = 48
/** A level further than this from the last price would flatten the line; it is left off. */
const MAX_LEVEL_DISTANCE = 0.3

export interface ChartLevel {
  label: string
  price: number
  tone: 'entry' | 'danger' | 'warn' | 'target'
}

const TONE_STROKE: Record<ChartLevel['tone'], string> = {
  entry: 'text-foreground/45',
  danger: 'text-chart-red',
  warn: 'text-ox-gold',
  target: 'text-chart-mint',
}

/**
 * The last two days of an instrument's hourly closes with the levels that matter for an idea
 * drawn across it — entry, liquidation, a proposed stop — so the reasoning has the picture next
 * to it. The line draws itself in; the levels slide in after it.
 */
export function HomePriceChart({
  symbol,
  exchange,
  levels,
  className,
}: {
  symbol: string
  exchange: Exchange
  levels: readonly ChartLevel[]
  className?: string
}) {
  const fmt = useNumberFormat()
  const [range] = useState(() => {
    const to = Math.floor(Date.now() / 1000)
    return { from: String(to - BARS * HOUR_S), to: String(to) }
  })
  const { data, isPending } = useChartData({
    symbol,
    exchange,
    timeframe: HOUR_S,
    from: range.from,
    to: range.to,
    limit: BARS,
    families: [CandleFamily.CANDLE_FAMILY_CORE],
  })
  const closes = useMemo(
    () => (data?.candles?.close ?? []).filter((value) => Number.isFinite(value) && value > 0),
    [data]
  )

  const geometry = useMemo(() => {
    if (closes.length < 2) return null
    const last = closes[closes.length - 1]
    const shown = levels.filter(
      (level) => level.price > 0 && Math.abs(level.price - last) / last <= MAX_LEVEL_DISTANCE
    )
    const values = [...closes, ...shown.map((level) => level.price)]
    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = max - min || last * 0.01
    const y = (v: number) => PAD_Y + (1 - (v - min) / span) * (HEIGHT - PAD_Y * 2)
    const x = (i: number) => (i / (closes.length - 1)) * WIDTH
    const line = `M${closes.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('L')}`
    return {
      line,
      rising: last >= closes[0],
      last,
      lastY: y(last),
      levels: shown.map((level) => ({ ...level, y: y(level.price) })),
    }
  }, [closes, levels])

  if (isPending) return <Skeleton className={cn('h-30 w-full', className)} />
  if (!geometry) return <div className={cn('h-30', className)} />

  return (
    <div className={cn('relative h-30 w-full', className)}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        className="h-full w-full overflow-visible"
      >
        {geometry.levels.map((level, index) => (
          <motion.line
            key={level.label}
            x1={0}
            x2={WIDTH}
            y1={level.y}
            y2={level.y}
            stroke="currentColor"
            strokeWidth={1}
            strokeDasharray="4 4"
            vectorEffect="non-scaling-stroke"
            className={TONE_STROKE[level.tone]}
            initial={{ opacity: 0, pathLength: 0 }}
            animate={{ opacity: 1, pathLength: 1 }}
            transition={{ duration: 0.5, delay: 0.5 + index * 0.08, ease: HOME_EASE }}
          />
        ))}
        <motion.path
          d={geometry.line}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          className={geometry.rising ? 'text-chart-mint' : 'text-chart-red'}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, ease: HOME_EASE }}
        />
      </svg>
      {geometry.levels.map((level, index) => (
        <motion.span
          key={level.label}
          className={cn(
            'bg-card absolute right-0 -translate-y-1/2 rounded-xs px-1 text-[10px] leading-4',
            TONE_STROKE[level.tone]
          )}
          style={{ top: `${(level.y / HEIGHT) * 100}%` }}
          initial={{ opacity: 0, x: 6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.7 + index * 0.08, ease: HOME_EASE }}
        >
          {level.label} {fmt.number(level.price, { maximumSignificantDigits: 6 })}
        </motion.span>
      ))}
    </div>
  )
}
